import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  FormCheck,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap';
import Select from 'react-select';
import LoadingOverlay from '../LoadingOverlay';
import AddAllStaffModal from '../modals/AddAllStaffModal';
import DeletePropertyUserModal from '../modals/DeletePropertyUserModal';

function PropertyPermissions(props) {
  const [loading, setLoading] = useState(false);

  const propertyUserProperties = useMemo(
    () => props.property.user_properties,
    [props.property]
  );

  const handleAddAllStaffConfirmation = () => {
    return fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/user-property/batch`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          SeasonalDatabase: props.selectedDatabase,
          Authorization: `Bearer ${props.bearerToken}`
        },
        body: JSON.stringify({
          property_id: props.property.id
        })
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          const property = props.property;
          const userProperties = [...property.user_properties, ...data];
          property.user_properties = userProperties;
          props.update(property);
        }
      });
  };

  const handleDeleteUserPropertyConfirmation = async (userPropertyId) => {
    const userPropertyDeleteRes = await fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/user-property/${userPropertyId}`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          SeasonalDatabase: props.selectedDatabase,
          Authorization: `Bearer ${props.bearerToken}`
        }
      }
    );
    if (userPropertyDeleteRes.ok) {
      try {
        const status = userPropertyDeleteRes.status;
        if (status !== 204) return;
        const property = props.property;
        const newUserProperties = property.user_properties.filter(
          (up) => up.id !== userPropertyId
        );
        property.user_properties = newUserProperties;
        props.update(property);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error(err);
      }
    } else if (userPropertyDeleteRes.status === 401) props.refreshBearerToken();
    else throw Error(userPropertyDeleteRes.statusText);
  };

  return (
    <Card>
      <Card.Header
        style={{ height: '63px' }}
        className='d-flex align-items-center'
      >
        Property Permissions
      </Card.Header>
      <Card.Body>
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              {props.propertyRoles.map((propertyRole) => {
                return <th key={propertyRole.id}>{propertyRole.name}</th>;
              })}
              <td />
            </tr>
          </thead>
          <tbody>
            {props.property.user_properties.map((userProperty) => {
              if (userProperty.user_type === 'fruition') {
                const user = props.users.find(
                  (user) => user.id === userProperty.user_id
                );
                if (user) {
                  return (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      {props.propertyRoles.map((propertyRole) => {
                        const hasPropertyRole =
                          userProperty.property_roles.some(
                            (userPropertyRole) =>
                              userPropertyRole.id === propertyRole.id
                          );
                        return (
                          <th
                            key={user.id + '-' + propertyRole.id}
                            className='text-center'
                          >
                            <FormCheck
                              custom
                              type='checkbox'
                              label=''
                              id={user.id + '-' + propertyRole.id}
                              checked={hasPropertyRole}
                              onChange={() => {
                                setLoading(true);
                                fetch(
                                  process.env.REACT_APP_PROPERTIES_API_URL +
                                    'api/user-property/' +
                                    userProperty.id +
                                    '/' +
                                    (hasPropertyRole ? 'detach' : 'attach') +
                                    '/' +
                                    propertyRole.id,
                                  {
                                    method: 'PUT',
                                    headers: {
                                      Accept: 'application/json',
                                      Authorization:
                                        'Bearer ' + props.bearerToken,
                                      SeasonalDatabase: props.selectedDatabase
                                    }
                                  }
                                )
                                  .then((res) => {
                                    if (res.ok) return res.json();
                                    else if (res.status === 401)
                                      props.refreshBearerToken();
                                    else throw Error(res.statusText);
                                  })
                                  .then((data) => {
                                    if (data) {
                                      props.update(data);
                                    }
                                  })
                                  .catch((err) => {
                                    if (process.env.NODE_ENV === 'development')
                                      console.error(err);
                                  })
                                  .finally(() => {
                                    setLoading(false);
                                  });
                              }}
                            />
                          </th>
                        );
                      })}
                      {userProperty.property_roles.length === 0 ? (
                        <td className='p-0 align-middle'>
                          <DeletePropertyUserModal
                            user={user}
                            property={props.property}
                            userProperty={userProperty}
                            confirmationHandler={
                              handleDeleteUserPropertyConfirmation
                            }
                          />
                        </td>
                      ) : (
                        <td className='p-0'></td>
                      )}
                    </tr>
                  );
                }
              }
            })}
          </tbody>
        </Table>
        <Formik
          initialValues={{
            users: props.users,
            property_id: props.property.id,
            user_type: 'fruition',
            user_id: ''
          }}
          validate={(values) => {
            const errors = {};

            if (!values.user_id) {
              errors.user_id = 'Please select a user';
            }

            return errors;
          }}
          onSubmit={(values, actions) => {
            fetch(
              process.env.REACT_APP_PROPERTIES_API_URL + 'api/user-property',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  Authorization: 'Bearer ' + props.bearerToken,
                  SeasonalDatabase: props.selectedDatabase
                },
                body: JSON.stringify({
                  property_id: values.property_id,
                  user_type: values.user_type,
                  user_id: values.user_id
                })
              }
            )
              .then((res) => {
                if (res.ok) return res.json();
                else if (res.status === 401) props.refreshBearerToken();
                else throw Error(res.statusText);
              })
              .then((data) => {
                if (data) {
                  props.update(data);
                }
              })
              .catch((err) => {
                if (process.env.NODE_ENV === 'development') console.error(err);
              })
              .finally(() => {
                actions.setSubmitting(false);
              });
          }}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <Form.Group controlId='user_id'>
                <InputGroup>
                  <Select
                    onChange={(option) =>
                      props.setFieldValue('user_id', option.value)
                    }
                    options={props.values.users
                      .filter(
                        (user) =>
                          !propertyUserProperties.some(
                            (userProperty) => userProperty.user_id == user.id
                          )
                      )
                      .map((user) => {
                        return {
                          value: user.id,
                          label: user.user ? user.user.name : user.name
                        };
                      })}
                    styles={{
                      container: (styles) => ({ ...styles, flex: '1 1 auto' })
                    }}
                    isLoading={props.isSubmitting}
                  />
                  <InputGroup.Append>
                    <Button
                      variant='outline-success'
                      className='rounded ml-1'
                      type='submit'
                      disabled={props.isSubmitting}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </Form.Group>
            </Form>
          )}
        </Formik>
        {/** Add all staff modal */}
        <Row>
          <Col>
            <AddAllStaffModal
              handleAddAllStaffConfirmation={handleAddAllStaffConfirmation}
            />
          </Col>
        </Row>
        {loading ? <LoadingOverlay /> : null}
      </Card.Body>
    </Card>
  );
}

PropertyPermissions.propTypes = {
  // Authentication
  selectedDatabase: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  // Property
  property: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,

  // Other data
  users: PropTypes.array.isRequired,
  propertyRoles: PropTypes.array.isRequired
};

export default PropertyPermissions;
